import { useState } from "react";
import { folderLabel } from "../home";
import { AddToPlan } from "../components/PlanEdit";
import type { CoursePlan, Need, PlanLesson, PlanUnit, WorkFolder } from "../home";

// The sidebar IS the plan: class, unit, lesson, for this school year.
//
// WHY THE NAV IS THE CURRICULUM. The old sidebar listed our pipeline — Student work, How work
// arrived, Release — and hung a learner-activity picker above it. The picker was the plan a second
// time, in a worse form: a flat list of assignments with no sense of sequence, sitting above tabs
// that described stages of our processing. A teacher's year is not a pipeline; it is a sequence of
// lessons, and the thing they mean by "where am I" is a position in that sequence.
//
// So the plan moved into the nav and the picker went away, because choosing a lesson IS choosing
// the assignment. Nothing is named twice.
//
// WHAT IS COMPRESSED. Everything except where the class is. A unit that is not the current one
// collapses to its title; the current unit shows its lessons. A teacher opens this to answer
// "what am I teaching" and occasionally "what did we do", and the second question is one click
// away rather than thirty lines of scrolling.

export interface Props {
  courses: CoursePlan[];
  /** The lesson currently open, by its activity's id — which is what the URL carries. */
  activeHref: string | null;
  onOpen: (href: string) => void;
  /** The cross-cohort queue, so the tree can say which folder is holding work. */
  needs?: Need[];
  /** The plan changed: re-read it. */
  onChanged?: () => void;
}

/** Where the cohort is: the lesson collecting work, or failing that the last one taught. */
export function currentLesson(c: CoursePlan): PlanLesson | null {
  const lessons = c.units.flatMap((u) => u.lessons);
  return lessons.find((l) => l.state === "collecting")
      ?? [...lessons].reverse().find((l) => l.state !== "not_taught")
      ?? null;
}

/** Where to LAND in a cohort: the latest collection of the lesson it is on.
 *
 *  A lesson with a draft and a final has no page of its own — its scores belong to one collection
 *  or the other — so landing needs the collection, not the lesson. */
export function landingHref(c: CoursePlan): string | null {
  const here = currentLesson(c);
  if (!here) return null;
  return here.work.length > 0 ? here.work[here.work.length - 1].href : here.href ?? null;
}

function unitOf(c: CoursePlan, lesson: PlanLesson | null): PlanUnit | null {
  if (!lesson) return null;
  return c.units.find((u) => u.lessons.some((l) => l.lesson_id === lesson.lesson_id)) ?? null;
}

// NO DECORATIVE MARKS. A tick beside some lessons and a dot beside others is a vocabulary
// nobody was taught: asked what the dot meant, the answer was "where the class is", which the
// open row already says. The only mark left in this column is the one that means WORK IS
// WAITING, so a symbol here has exactly one meaning.

function Folder({ folder, active, onOpen, showSection, needs }: {
  folder: WorkFolder; active: boolean; onOpen: (href: string) => void; needs: number;
  /** Only where the course has more than one section. On a single-section course the label is
   *  the same word under every folder, which is noise. */
  showSection: boolean;
}) {
  return (
    <li>
      <button className={`pn-folder${active ? " pn-active" : ""}`}
              aria-current={active ? "page" : undefined}
              onClick={() => onOpen(folder.href)}>
        {/* What is waiting on a person INSIDE this folder. Every paper that needs a judgement
            belongs to one, so this is where the count belongs: a teacher looking for work to do
            reads the tree, not a strip above it. */}
        {needs > 0 && <span className="pn-needs" title={`${needs} waiting on you`}>{needs}</span>}
        {/* What the teacher called it, or a fallback that is not an id. */}
        <span className="pn-title">{folderLabel(folder.iteration) || "Student work"}</span>
        {showSection && <span className="pn-for">{folder.section_label}</span>}
        {folder.handed_in && (
          <span className="pn-count">{folder.handed_in.have}/{folder.handed_in.expected}</span>
        )}
      </button>
    </li>
  );
}

function Lesson({ lesson, active, activeHref, onOpen, onToggle, open: openProp, showSection,
                 needsFor }: {
  lesson: PlanLesson; active: boolean; activeHref: string | null;
  onOpen: (href: string) => void;
  onToggle?: () => void;
  open?: boolean;
  showSection: boolean;
  needsFor: (href: string) => number;
}) {
  const lessonHref = `/writing/l/${encodeURIComponent(lesson.lesson_id)}`;
  const waiting = lesson.work.reduce((n, w) => n + needsFor(w.href), 0);
  // A lesson is a PLAN. Its row goes to the lesson; it never opens into scores, however many
  // collections there happen to be — a row that meant "the plan" or "the work" depending on how
  // many folders existed would be the same object saying two things.
  //
  // NOTHING HERE DESCRIBES ANYTHING. The unit's question and the lesson's summary used to sit
  // under their rows, one line each, "to navigate by". They are not navigation: a teacher reads
  // this column to find where they are, and prose in it is the thing they have to read past. It
  // is all on the lesson's and the unit's own pages, which is what those pages are for.
  // Greyed because it has not been taught, not because there is nothing to expand. The two were
  // the same test while the nav also revealed a lesson's summary; now that it reveals only
  // collections, a taught lesson whose folder nobody has made would have gone grey.
  const ahead = lesson.state === "not_taught";
  // The collections show under the lesson you are in, and under any lesson that has more than
  // one — a draft and a final are two different sets of papers, and a lesson that silently
  // opened only the final would hide the other. Everything else stays compressed.
  // Open by default where the class IS, so a teacher lands looking at the work in front of them.
  const open = (openProp ?? active) && lesson.work.length > 0;
  return (
    <li>
      {/* A twist to show the collections, a title to open the lesson. Same split as the unit:
          expanding and going somewhere are different intentions and need different targets. */}
      <div className="pn-row">
      {lesson.work.length > 0 && (
        <button className="pn-twist pn-twist-lesson" aria-expanded={open}
                aria-label={open ? "Hide student work" : "Show student work"}
                onClick={() => onToggle?.()}>
          <span className="pn-caret" data-open={open} aria-hidden="true" />
        </button>
      )}
      <button
        // Where the class is, marked. Untaught lessons are greyed rather than hidden: what is
        // coming is part of knowing where you are.
        className={`pn-lesson${active ? " pn-here" : ""}${ahead ? " pn-ahead" : ""}`}
        aria-current={activeHref === lessonHref ? "page" : undefined}
        onClick={() => onOpen(lessonHref)}>
        {/* Rolled up from the folders while they are hidden, so a collapsed lesson still says
            there is something in it. */}
        {!open && waiting > 0 && (
          <span className="pn-needs" title={`${waiting} waiting on you`}>{waiting}</span>
        )}
        <span className="pn-ord">{lesson.ordinal ?? ""}</span>
        <span className="pn-title">{lesson.title}</span>
        {/* The count belongs to the folder when they are shown, not repeated on both. */}
        {lesson.handed_in && !open && (
          <span className="pn-count">{lesson.handed_in.have}/{lesson.handed_in.expected}</span>
        )}
      </button>
      </div>
      {open && (
        <ul className="pn-folders">
          {lesson.work.map((w) => (
            <Folder key={w.activity_id} folder={w} onOpen={onOpen} showSection={showSection}
                    needs={needsFor(w.href)}
                    active={Boolean(activeHref && w.href === activeHref)} />
          ))}
        </ul>
      )}
    </li>
  );
}

function Unit({ unit, open, active, onToggle, activeHref, onOpen, showSection, needsFor,
                classId, onChanged }: {
  unit: PlanUnit; open: boolean; active: boolean; onToggle: () => void;
  activeHref: string | null; onOpen: (href: string) => void; showSection: boolean;
  needsFor: (href: string) => number;
  classId: string; onChanged: () => void;
}) {
  // Which lessons a teacher has opened, remembered per unit: reading what a lesson is about
  // should not close the one they are teaching.
  const [openLessons, setOpenLessons] = useState<Record<string, boolean>>({});
  const unitHref = `/writing/u/${encodeURIComponent(unit.unit_id ?? "")}`;
  const here = activeHref === unitHref;
  const taught = unit.lessons.filter((l) => l.state !== "not_taught").length;
  return (
    <li className={`pn-unit${active || here ? " pn-unit-here" : ""}`}>
      {/* The caret expands; the title opens the unit. Two jobs, two targets — a title that only
          expanded left nowhere to read the thing it named. */}
      <div className="pn-row">
      <button className="pn-twist" aria-expanded={open} aria-label={open ? "Collapse" : "Expand"}
              onClick={onToggle}>
        <span className="pn-caret" data-open={open} aria-hidden="true" />
      </button>
      <button className="pn-unithead" aria-current={here ? "page" : undefined}
              onClick={() => unit.unit_id && onOpen(unitHref)}>
        <span className="pn-title">{unit.title ?? "Lessons"}</span>
        {/* A collapsed unit still says how far it got, so compressing it hides the detail and
            not the fact. */}
        {!open && <span className="pn-count">{taught}/{unit.lessons.length}</span>}
      </button>
      {/* ON THE HIGHLIGHTED UNIT, and nowhere else — the same test as the highlight itself, so
          the plus is on the unit that looks like the one you are in. A plus beside every unit in
          a term is a column of pluses; beside that one it is the next thing you might do. */}
      {(here || active) && unit.unit_id && (
        <AddToPlan kind="lesson" classId={classId}
                   unitId={unit.unit_id} onAdded={onChanged} onOpen={onOpen} compact />
      )}
      </div>
      {open && (
        <ul className="pn-lessons">
          {unit.lessons.map((l) => (
            <Lesson key={l.lesson_id} lesson={l} onOpen={onOpen} activeHref={activeHref}
                    showSection={showSection} needsFor={needsFor}
                    active={Boolean(activeHref
                      && (activeHref === `/writing/l/${encodeURIComponent(l.lesson_id)}`
                          || l.work.some((w) => w.href === activeHref)))}
                    open={openLessons[l.lesson_id]}
                    onToggle={() => setOpenLessons((o) => ({
                      ...o, [l.lesson_id]: !(o[l.lesson_id]
                        ?? l.work.some((w) => w.href === activeHref)),
                    }))} />
          ))}
        </ul>
      )}
    </li>
  );
}

function ClassTree({ c, expanded, onToggle, activeHref, onOpen, needsFor, onChanged }: {
  c: CoursePlan; expanded: boolean; onToggle: () => void;
  activeHref: string | null; onOpen: (href: string) => void;
  needsFor: (href: string) => number;
  onChanged: () => void;
}) {
  const here = currentLesson(c);
  const hereUnit = unitOf(c, here);
  // Which units are open is per-class local state: opening Unit 1 to check what you did should
  // not close the unit you are teaching.
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const isOpen = (u: PlanUnit) =>
    open[u.unit_id ?? "loose"] ?? (u.unit_id === hereUnit?.unit_id);

  return (
    <div className={`pn-class${expanded ? "" : " pn-collapsed"}`}>
      {/* THE COURSE, and only the course. It read "US Government · Gov 11B, period 4" — the
          course, then a section whose name repeated it. A section is a fact about the work, and
          it appears on the folders that belong to one. */}
      <div className="pn-row">
        <button className="pn-classhead" aria-expanded={expanded} onClick={onToggle}>
          <span className="pn-caret" data-open={expanded} aria-hidden="true" />
          <span className="pn-classname">{c.course_name}</span>
        </button>
        {/* A UNIT GOES INTO A COURSE, so the control is on the course. The nav is the plan; the
            place to change the plan is the place you read it, not a page you have to open
            first. */}
        {expanded && (
          <AddToPlan kind="unit" classId={c.class_id}
                     onAdded={onChanged} onOpen={onOpen} compact />
        )}
      </div>
      {expanded && (
        <ul className="pn-units">
          {c.units.map((u) => (
            <Unit key={u.unit_id ?? "loose"} unit={u} showSection={c.sections.length > 1}
                  needsFor={needsFor} classId={c.class_id}
                  onChanged={onChanged}
                  open={isOpen(u)} active={u.unit_id === hereUnit?.unit_id}
                  onToggle={() => setOpen((o) => ({
                    ...o, [u.unit_id ?? "loose"]: !isOpen(u),
                  }))}
                  activeHref={activeHref} onOpen={onOpen} />
          ))}
          {c.units.length === 0 && (
            <li className="pn-empty">This course has no units yet.</li>
          )}
        </ul>
      )}
    </div>
  );
}

export function PlanNav({ courses, activeHref, onOpen, needs = [],
                          onChanged = () => {} }: Props) {
  // What is waiting, by the screen that fixes it. The queue's rows already carry that href, so
  // this is the same evidence read in the place a teacher is looking for work.
  const waiting = new Map<string, number>();
  for (const n of needs) waiting.set(n.href, (waiting.get(n.href) ?? 0) + 1);
  const needsFor = (href: string) => waiting.get(href) ?? 0;
  // The course holding the open lesson starts expanded; the others collapse to a line. Two full
  // trees is more nav than plan, and a teacher is in one course at a time.
  const holding = courses.find((c) => c.units.some((u) =>
    u.lessons.some((l) => l.work.some((w) => w.href === activeHref)
      || activeHref === `/writing/l/${encodeURIComponent(l.lesson_id)}`)));
  const [shown, setShown] = useState<string | null>(null);
  const openCourse = shown ?? holding?.class_id ?? courses[0]?.class_id ?? null;

  if (courses.length === 0) {
    return (
      <nav className="pn" aria-label="Curriculum plan">
        <p className="pn-empty">
          None of your classes is following a curriculum plan yet, so there is no sequence to show.
        </p>
      </nav>
    );
  }

  return (
    <nav className="pn" aria-label="Curriculum plan">
      {courses.map((c) => (
        <ClassTree key={c.class_id} c={c}
                   expanded={c.class_id === openCourse}
                   onToggle={() => setShown(c.class_id === openCourse ? "" : c.class_id)}
                   activeHref={activeHref} onOpen={onOpen} needsFor={needsFor}
                   onChanged={onChanged} />
      ))}
    </nav>
  );
}
